import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useTranslation } from "react-i18next";
import { getStudioStats } from "@/lib/agents.functions";
import { STUDIO_AGENTS } from "@/lib/ai/_agents-studio";
import { modelLabel } from "@/lib/ai/_ai-pricing";
import { usePlan } from "@/hooks/use-plan";
import { useLocaleFmt } from "@/lib/i18n/format";
import { Loader2, Activity, Coins, Cpu, BarChart3 } from "lucide-react";

export const Route = createFileRoute("/_authenticated/app/agenti/dashboard")({
  component: StudioDashboard,
});

function StudioDashboard() {
  const { t } = useTranslation();
  const { localeTag } = useLocaleFmt();
  const { plan } = usePlan();
  const fetchStats = useServerFn(getStudioStats);
  const { data, isLoading } = useQuery({
    queryKey: ["studio-stats"],
    queryFn: () => fetchStats(),
    enabled: plan === "studio",
    refetchOnWindowFocus: false,
  });

  if (plan !== "studio") {
    return <div className="p-8 text-center text-muted-foreground">{t("pages.agenti.dashOnly")}</div>;
  }
  if (isLoading || !data) {
    return <div className="flex h-full items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  const agentName = (id: string) => STUDIO_AGENTS.find((a) => a.id === id)?.name ?? id;
  const maxSessions = Math.max(1, ...data.perAgent.map((p: any) => p.sessions));

  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-8">
      <h1 className="text-3xl font-bold text-foreground">{t("pages.agenti.dashTitle")}</h1>
      <p className="mt-2 text-muted-foreground">{t("pages.agenti.dashSubtitle")}</p>

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={<Activity className="h-5 w-5" />} label={t("pages.agenti.dashSessions")} value={String(data.totals.sessions)} />
        <StatCard icon={<Cpu className="h-5 w-5" />} label={t("pages.agenti.dashTokens")} value={`${((data.totals.tokensIn + data.totals.tokensOut) / 1000).toFixed(1)}K`} />
        <StatCard icon={<Coins className="h-5 w-5" />} label={t("pages.agenti.dashCostEst")} value={`$${data.totals.costUsd.toFixed(2)}`} />
        <StatCard icon={<BarChart3 className="h-5 w-5" />} label={t("pages.agenti.dashTopAgent")} value={data.perAgent[0] ? agentName(data.perAgent[0].agentId).split(" ")[0] : "—"} />
      </div>

      <section className="mt-8 rounded-2xl border-2 border-border-strong bg-background p-5">
        <h2 className="text-[15px] font-bold uppercase tracking-wider text-foreground">{t("pages.agenti.dashByAgent")}</h2>
        {data.perAgent.length === 0 ? (
          <p className="mt-3 text-muted-foreground">{t("pages.agenti.dashNoData")}</p>
        ) : (
          <div className="mt-4 space-y-2">
            {data.perAgent.map((p: any) => (
              <div key={p.agentId}>
                <div className="flex items-center justify-between text-[13px]">
                  <span className="text-foreground">{agentName(p.agentId)}</span>
                  <span className="text-muted-foreground">{p.sessions} · ${p.costUsd.toFixed(2)}</span>
                </div>
                <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-secondary">
                  <div className="h-full bg-primary" style={{ width: `${(p.sessions / maxSessions) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="mt-6 rounded-2xl border-2 border-border-strong bg-background p-5">
        <h2 className="text-[15px] font-bold uppercase tracking-wider text-foreground">{t("pages.agenti.dashRecent")}</h2>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="text-left text-muted-foreground">
              <tr>
                <th className="py-2">{t("pages.agenti.dashColDate")}</th>
                <th>{t("pages.agenti.dashColAgent")}</th>
                <th>{t("pages.agenti.dashColModel")}</th>
                <th className="text-right">{t("pages.agenti.dashColTokensOut")}</th>
                <th className="text-right">{t("pages.agenti.dashColCost")}</th>
              </tr>
            </thead>
            <tbody>
              {data.recent.map((r: any, i: number) => (
                <tr key={i} className="border-t border-border-strong">
                  <td className="py-2">{new Date(r.created_at).toLocaleString(localeTag, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</td>
                  <td>{agentName(r.agent_id)}</td>
                  <td className="text-muted-foreground">{modelLabel(r.model_used)}</td>
                  <td className="text-right">{r.tokens_out}</td>
                  <td className="text-right">${r.cost_usd.toFixed(4)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function StatCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="rounded-2xl border-2 border-border-strong bg-background p-4">
      <div className="flex items-center gap-2 text-muted-foreground">{icon}<span className="text-[12px] font-semibold uppercase">{label}</span></div>
      <div className="mt-2 text-2xl font-bold text-foreground">{value}</div>
    </div>
  );
}
